import {
  cnpjToApiPayload,
  formatCnpjMasked,
  normalizeCnpjAlphanumeric,
} from '@solfacil/frontend-utils/cnpj';
import { useQuery } from '@tanstack/vue-query';
import { computed, type Ref } from 'vue';

import type { PartnerBlocklistApi } from '~/services/partner-blocklist-api/partner-blocklist-api.types';
import type { PersonAPI } from '~/services/person-api/person-api.types';

import partnerBlocklistApi from '~/services/partner-blocklist-api/partner-blocklist-api';
import personApi from '~/services/person-api/person-api';
import { queryKeys } from '~/store/queryClient';

type PartnerDetailsResult = {
  blocklist: null | PartnerBlocklistApi.BlocklistRecord;
  blocklistError: boolean;
  formattedDocument: string;
  partner: PersonAPI.GetPartnerByIdResponse;
};

// eslint-disable-next-line perfectionist/sort-modules
export type UsePartnerDetailsData = null | PartnerDetailsResult;

export default function usePartnerDetails(partnerId: Ref<null | number | undefined>) {
  const queryKey = computed(() => queryKeys.PARTNER.DETAILS(partnerId.value ?? 'unknown'));

  const queryFn = async (): Promise<null | PartnerDetailsResult> => {
    if (!partnerId.value) return null;

    const partner = await personApi.partner.getById({
      partner_id: partnerId.value,
      with_sfplus: true,
    }) as PartnerDetailsResult['partner'];

    const hasDocument = Boolean(partner?.document);
    const formattedDocument = hasDocument
      ? formatCnpjMasked(normalizeCnpjAlphanumeric(partner.document))
      : '';
    const requestDocument = hasDocument ? cnpjToApiPayload(partner.document) : '';

    let blocklist: null | PartnerBlocklistApi.BlocklistRecord = null;
    let blocklistError = false;

    if (requestDocument) {
      try {
        const blocklistResponse = await partnerBlocklistApi.getByDocument(requestDocument);
        blocklist = blocklistResponse[0] ?? null;
      } catch (error) {
        blocklist = null;
        blocklistError = true;
      }
    }

    return {
      blocklist,
      blocklistError,
      formattedDocument,
      partner,
    };
  };

  const query = useQuery({
    enabled: computed(() => Boolean(partnerId.value)),
    queryFn,
    queryKey,
  });

  return query;
}
